<template>
  <div v-if="totalPages > 1" class="flex items-center justify-between gap-1 mt-4">
    <div class="flex flex-row items-center justify-start gap-1">
      <button
        class="button small paginate-btn w-[32px] h-[32px] rtl:-scale-x-100"
        :class="currentPage === 1 ? 'disabled' : ''"
        :disabled="currentPage === 1"
        @click="goToPage(1)"
      >
        <svg
          width="9"
          height="8"
          viewBox="0 0 9 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.76917 7.53833L0 3.76917L3.76917 0L4.47167 0.7025L1.41533 3.76917L4.47167 6.83583L3.76917 7.53833ZM8.0025 7.53833L4.23333 3.76917L8.0025 0L8.705 0.7025L5.64867 3.76917L8.705 6.83583L8.0025 7.53833Z"
            fill="#6B7280"
          />
        </svg>
      </button>
      <button
        class="button small paginate-btn w-[32px] h-[32px] rtl:-scale-x-100"
        :class="currentPage === 1 ? 'disabled' : ''"
        :disabled="currentPage === 1"
        @click="goToPage(currentPage - 1)"
      >
        <svg
          width="5"
          height="8"
          viewBox="0 0 5 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.76917 7.53833L0 3.76917L3.76917 0L4.47167 0.7025L1.405 3.76917L4.47167 6.83583L3.76917 7.53833Z"
            fill="#6B7280"
          />
        </svg>
      </button>
      <template v-for="(page, index) in visiblePages">
        <span
          v-if="page === '...'"
          :key="`ellipsis-${index}`"
          class="px-2 paginate-btn-without-hover w-[32px] h-[32px] text-[#525252] text-sm"
          >...</span
        >
        <button
          v-else
          :key="`page-${page}`"
          class="button small paginate-btn min-w-[32px] h-[32px]"
          :class="{ 'current-page': page === currentPage }"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
      </template>
      <button
        class="button small paginate-btn w-[32px] h-[32px] rtl:-scale-x-100"
        :disabled="currentPage === totalPages"
        :class="currentPage === totalPages ? 'disabled' : ''"
        @click="goToPage(currentPage + 1)"
      >
        <svg
          width="5"
          height="8"
          viewBox="0 0 5 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.06667 3.76917L0 0.7025L0.7025 0L4.47167 3.76917L0.7025 7.53833L0 6.83583L3.06667 3.76917Z"
            fill="#6B7280"
          />
        </svg>
      </button>
      <button
        class="button small paginate-btn w-[32px] h-[32px] rtl:-scale-x-100"
        :disabled="currentPage === totalPages"
        :class="currentPage === totalPages ? 'disabled' : ''"
        @click="goToPage(totalPages)"
      >
        <svg
          width="9"
          height="8"
          viewBox="0 0 9 8"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.05633 3.76917L0 0.7025L0.7025 0L4.47167 3.76917L0.7025 7.53833L0 6.83583L3.05633 3.76917ZM7.28967 3.76917L4.23333 0.7025L4.93583 0L8.705 3.76917L4.93583 7.53833L4.23333 6.83583L7.28967 3.76917Z"
            fill="#6B7280"
          />
        </svg>
      </button>
    </div>
    <span class="text-xs text-gray-700 pr-1">
      <strong>{{ firstIndex }}</strong> -
      <strong>{{ lastIndex }}</strong>
      {{ $t('TEAMS_SETTINGS.LIST.PAGINATION.OF') }}
      <strong>{{ totalCount }}</strong>
      {{ $t('TEAMS_SETTINGS.LIST.PAGINATION.ITEMS') }}
    </span>
  </div>
</template>

<script>
export default {
  props: {
    currentPage: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 15,
    },
    totalCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    },
    firstIndex() {
      return this.pageSize * (this.currentPage - 1) + 1;
    },
    lastIndex() {
      return Math.min(this.totalCount, this.pageSize * this.currentPage);
    },
    visiblePages() {
      const pages = [];
      const delta = 1;
      const left = this.currentPage - delta;
      const right = this.currentPage + delta;

      let last = 0;
      for (let i = 1; i <= this.totalPages; i += 1) {
        if (i === 1 || i === this.totalPages || (i >= left && i <= right)) {
          if (last && i - last > 1) pages.push('...');
          pages.push(i);
          last = i;
        }
      }
      return pages;
    },
  },
  methods: {
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }
      this.$emit('page-change', page);
    },
  },
};
</script>

<style lang="scss" scoped>
.paginate-btn-without-hover {
  @apply rounded-[8px] flex flex-col justify-center items-center cursor-default;
  @apply bg-white border border-[#F5F5F5] text-[#525252];
  @apply focus:bg-white focus:border-[#F5F5F5] focus:text-[#525252];
  @apply hover:bg-white hover:border-[#F5F5F5] hover:text-[#525252];
}

.paginate-btn {
  @apply rounded-[8px] flex flex-col justify-center items-center;
  @apply bg-white border border-[#F5F5F5] text-[#525252];
  @apply focus:bg-white focus:border-[#F5F5F5] focus:text-[#525252];
  @apply hover:bg-white hover:border-[#4BCE97] hover:text-[#216E4E];

  &.current-page {
    @apply bg-green-500 border-green-500 text-white;
  }

  &.disabled {
    @apply bg-[#F5F5F5] opacity-100 text-[#A3A3A3];
    @apply focus:bg-[#F5F5F5] focus:border-[#F5F5F5] focus:text-[#A3A3A3];
    @apply hover:bg-[#F5F5F5] hover:border-[#F5F5F5] hover:text-[#A3A3A3];
  }
}
</style>
